import { useState, type FormEvent } from 'react'
import { Seo } from '../components/Seo'
import { PageHeader } from '../components/common/PageHeader'
import { company } from '../data/company'
import { request } from '../../api/client'

/**
 * Public, no-login account & data deletion (Google Play requirement). Reached at /delete-account.
 * Two steps against the @Public() backend endpoints: request a one-time code to the account's mobile,
 * then confirm — which immediately anonymises + removes the account. Not linked from the app UI; the
 * URL is given to Play Console and referenced in the Privacy Policy.
 */
type Step = 'enter' | 'verify' | 'done'

function errMessage(e: any): string {
  return (
    e?.response?.data?.error?.message ??
    e?.response?.data?.message ??
    e?.message ??
    'Something went wrong. Please try again.'
  )
}

export default function AccountDeletionPage() {
  const [step, setStep] = useState<Step>('enter')
  const [mobile, setMobile] = useState('')
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [hint, setHint] = useState('')
  const [done, setDone] = useState('')

  const inputCls =
    'w-full rounded-[9px] border border-line bg-surface px-4 py-2.5 text-content outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40'
  const primaryBtn =
    'inline-flex h-11 items-center justify-center rounded-[9px] bg-brand-800! px-5 text-[15px] font-semibold text-white! transition-colors hover:bg-brand-600! disabled:opacity-50'
  const dangerBtn =
    'inline-flex h-11 items-center justify-center rounded-[9px] bg-red-600 px-5 text-[15px] font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50'

  async function sendOtp(e: FormEvent) {
    e.preventDefault()
    setError('')
    setHint('')
    if (!mobile.trim()) {
      setError('Please enter your mobile number.')
      return
    }
    setLoading(true)
    try {
      const r: any = await request({
        url: '/public/account-deletion/request-otp',
        method: 'post',
        data: { mobile: mobile.trim() },
      })
      setStep('verify')
      // Development convenience only. This is a PUBLIC, unauthenticated page, so an echoed OTP printed
      // here would be on open display. The server withholds `devCode` outside development; the
      // `import.meta.env.PROD` check is statically replaced at build time so the branch — and the
      // string — never reach a production bundle at all.
      if (!import.meta.env.PROD && r?.devCode) setHint(`Test code: ${r.devCode}`)
    } catch (err) {
      setError(errMessage(err))
    } finally {
      setLoading(false)
    }
  }

  async function confirmDelete(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (code.trim().length !== 6) {
      setError('Enter the 6-digit code sent to your mobile.')
      return
    }
    setLoading(true)
    try {
      const r: any = await request({
        url: '/public/account-deletion/confirm',
        method: 'post',
        data: { mobile: mobile.trim(), code: code.trim() },
      })
      setDone(r?.message ?? 'Your account and personal data have been removed.')
      setStep('done')
    } catch (err) {
      setError(errMessage(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Seo
        title="Delete your account | GreenbayBio"
        description="Request deletion of your Greenbay account and the personal data associated with it."
        path="/delete-account"
      />
      <PageHeader
        eyebrow="Account"
        title="Delete your account"
        subtitle="Request deletion of your Greenbay account and the personal data associated with it."
      />

      <div className="container-x max-w-prose py-14">
        <div className="space-y-4 leading-relaxed text-content">
          <p>
            You can delete your Greenbay account and associated personal data here, without installing the app.
            We’ll verify it’s you by sending a one-time code to your registered mobile number.
          </p>
          <p className="text-muted">
            When you delete your account we remove your login, anonymise your personal details (such as your name,
            phone number and photos), and stop location tracking and notifications. Records we are legally required to
            keep — such as tax and invoice records — are retained in anonymised form. This cannot be undone.
          </p>
        </div>

        <div className="mt-8 rounded-2xl border border-line bg-surface p-6 shadow-sm">
          {step === 'enter' && (
            <form onSubmit={sendOtp} className="space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-content">Registered mobile number</span>
                <input
                  className={inputCls}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="e.g. +91 90000 00001"
                  value={mobile}
                  onChange={(ev) => setMobile(ev.target.value)}
                />
              </label>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button type="submit" className={primaryBtn} disabled={loading}>
                {loading ? 'Sending…' : 'Send verification code'}
              </button>
            </form>
          )}

          {step === 'verify' && (
            <form onSubmit={confirmDelete} className="space-y-4">
              <p className="text-sm text-muted">
                We sent a 6-digit code to <span className="font-medium text-content">{mobile}</span>. Enter it below to
                confirm deletion.
              </p>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-content">Verification code</span>
                <input
                  className={inputCls}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="6-digit code"
                  value={code}
                  onChange={(ev) => setCode(ev.target.value.replace(/\D/g, ''))}
                />
              </label>
              {hint && <p className="text-sm text-brand-700">{hint}</p>}
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex flex-wrap items-center gap-3">
                <button type="submit" className={dangerBtn} disabled={loading}>
                  {loading ? 'Deleting…' : 'Permanently delete my account'}
                </button>
                <button
                  type="button"
                  className="text-sm font-medium text-muted hover:text-content"
                  onClick={() => {
                    setStep('enter')
                    setCode('')
                    setError('')
                  }}
                >
                  Back
                </button>
              </div>
            </form>
          )}

          {step === 'done' && (
            <div className="space-y-3">
              <p className="text-lg font-semibold text-brand-800">Account deleted</p>
              <p className="text-content">{done}</p>
            </div>
          )}
        </div>

        <p className="mt-8 text-sm text-muted">
          Need help, or want to close a staff account? Contact us at {company.email} or {company.phoneDisplay}.
        </p>
      </div>
    </>
  )
}
